import { useEffect, useRef } from 'react'

/**
 * A ref for a region that may scroll (a fixed-height panel whose content can be long): while it actually overflows,
 * it gets `tabIndex=0`, so a keyboard user can focus and scroll it (axe `scrollable-region-focusable`); otherwise it
 * stays out of the tab order. Set imperatively, because whether it scrolls depends on the content and the viewport.
 */
export function useFocusableWhenScrollable<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => {
      const scrollable = el.scrollHeight > el.clientHeight + 1 || el.scrollWidth > el.clientWidth + 1
      if (scrollable) el.tabIndex = 0
      else el.removeAttribute('tabindex')
    }
    update()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(update)
    observer.observe(el)
    for (const child of el.children) observer.observe(child)
    return () => {
      observer.disconnect()
    }
  })
  return ref
}
